import type { FundListQuery, FundTrendsQuery } from '@/schemas/mutual-funds'

/**
 * Query key factory for Mutual funds.
 *
 * One branch per response shape, the rule `features/brokers/api/keys.ts`
 * states: stock-count and sector-count rows are both "a fund and a number",
 * and a shared key would let one tab hand the other its rows for a frame.
 *
 * `period` is the holdings-period label, or `null` for "the backend's latest"
 * — a different key from the latest label spelled out, on purpose: the two
 * are the same data only while no newer month has been filed.
 */
export const fundKeys = {
  all: ['mutual-funds'] as const,

  /** The fund directory, per Price vs NAV category and search. */
  list: (query: FundListQuery) => [...fundKeys.all, 'list', query] as const,

  /** The selectable holdings periods. No parameters. */
  periods: () => [...fundKeys.all, 'periods'] as const,

  // ── Analytics ─────────────────────────────────────────────────────────────

  summary: (period: string | null) => [...fundKeys.all, 'summary', period] as const,
  stockCount: (period: string | null) => [...fundKeys.all, 'stock-count', period] as const,
  popular: (period: string | null) => [...fundKeys.all, 'popular', period] as const,
  sectorCounts: (period: string | null) => [...fundKeys.all, 'sector-counts', period] as const,
  sectorBreakdown: (fund: string, period: string | null) =>
    [...fundKeys.all, 'sector-breakdown', fund, period] as const,
  /** `funds` arrives here already de-duplicated and sorted — see `fundTrendsQueryOptions`. */
  trends: (query: FundTrendsQuery) => [...fundKeys.all, 'trends', query] as const,

  // ── Holdings ──────────────────────────────────────────────────────────────

  industryHoldings: (period: string | null) =>
    [...fundKeys.all, 'industry-holdings', period] as const,
  sectorCompanies: (sector: string, period: string | null) =>
    [...fundKeys.all, 'sector-companies', sector, period] as const,
  monthlyHoldings: (period: string | null) =>
    [...fundKeys.all, 'monthly-holdings', period] as const,
  fundHoldings: (fund: string, period: string | null) =>
    [...fundKeys.all, 'fund-holdings', fund, period] as const,
  stockHolders: (symbol: string, period: string | null) =>
    [...fundKeys.all, 'stock-holders', symbol, period] as const,
  incomeStatement: (fund: string) => [...fundKeys.all, 'income-statement', fund] as const,
  navHistory: (fund: string, months: number) =>
    [...fundKeys.all, 'nav-history', fund, months] as const,

  dividends: () => [...fundKeys.all, 'dividends'] as const,
}
